import { api } from "./client";

export type RecommendationCard = {
  id: string;
  severity: "ok" | "warn" | "critical";
  title: string;
  message: string;
  action: string;
};

export type RuntimeSettingItem = {
  key: string;
  label: string;
  description: string;
  defaultValue: number;
  min: number;
  max: number;
  unit: string;
  value: number;
  isDefault: boolean;
};

export type SystemStatusResponse = {
  generated_at: string;
  db: {
    ping: { ok: boolean; latency_ms: number; error?: string };
    pool: { total: number; idle: number; waiting: number; max: number };
    server_connections: { total: number; active: number; idle: number } | null;
    size_bytes: number | null;
    size_limit_bytes: number;
    size_percent: number | null;
    top_tables: Array<{ table_name: string; bytes: number; approx_rows: number }>;
  };
  app: {
    uptime_seconds: number;
    node_version: string;
    memory_rss_mb: number;
    memory_heap_used_mb: number;
    memory_limit_mb: number;
    http: {
      requests_last_5m: number;
      requests_per_minute_5m: number;
      errors_5xx_last_5m: number;
      errors_4xx_last_5m: number;
      error_rate_5xx_percent_5m: number;
      avg_duration_ms_5m: number;
      max_duration_ms_5m: number;
      p95_duration_ms: number;
      event_loop_lag_ms: number;
      total_requests_since_start: number;
      per_minute: Array<{ minute_iso: string; count: number; errors_5xx: number; avg_ms: number }>;
    };
  };
  security: {
    events_last_24h: Array<{ event_type: string; c: number }>;
  };
  migrations: {
    applied_count: number;
    recent: Array<{ filename: string; applied_at: string }>;
  };
  observability: {
    sentry_enabled: boolean;
    health_shallow_path: string;
    health_deep_path: string;
    uptime_github_workflow: string;
  };
  runtime_settings: RuntimeSettingItem[];
  recommendations: RecommendationCard[];
};

export type ClientConfig = {
  notifications_poll_interval_ms: number;
  list_page_size: number;
};

export function fetchSystemStatus(): Promise<SystemStatusResponse> {
  return api.get<SystemStatusResponse>("/system-status");
}

export function updateRuntimeSetting(key: string, value: number): Promise<{ ok: boolean; settings: RuntimeSettingItem[] }> {
  return api.put<{ ok: boolean; settings: RuntimeSettingItem[] }>("/system-status/settings", { key, value });
}

export function fetchClientConfig(): Promise<ClientConfig> {
  return api.get<ClientConfig>("/system-status/client-config");
}
